import React, { useState, useEffect } from 'react';
import { 
  Paper, 
  Typography, 
  TextField, 
  Box, 
  Divider, 
  FormControl, 
  FormControlLabel, 
  FormLabel, 
  Radio, 
  RadioGroup, 
  Tooltip, 
  InputAdornment,
  Alert
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { QueueRunMode, QueueStopCondition, QueueConfig } from '../types/dex';

/**
 * 批量任务设置组件的属性接口
 */
interface BatchTaskSettingsProps {
  /** 队列配置 */
  queueConfig: QueueConfig;
  /** 是否禁用编辑 */
  disabled?: boolean;
  /** 队列配置更新回调 */
  onQueueConfigChange: (config: QueueConfig) => void;
  /** 任务是否正在运行 */
  taskRunning?: boolean;
}

/**
 * 批量任务设置组件
 * 
 * @param props 组件属性
 * @returns 批量任务设置组件
 */
const BatchTaskSettings: React.FC<BatchTaskSettingsProps> = ({
  queueConfig,
  disabled = false,
  onQueueConfigChange,
  taskRunning = false
}) => {
  // 本地状态，用于更新前的临时存储
  const [threads, setThreads] = useState<number>(queueConfig.threads);
  const [taskCount, setTaskCount] = useState<number>(queueConfig.taskCount);
  const [interval, setInterval] = useState<number>(queueConfig.interval);
  const [runMode, setRunMode] = useState<QueueRunMode>(queueConfig.runMode);
  const [stopCondition, setStopCondition] = useState<QueueStopCondition>(queueConfig.stopCondition);
  const [stopValue, setStopValue] = useState<number>(queueConfig.stopValue);

  // 当传入的配置变更时更新本地状态
  useEffect(() => {
    setThreads(queueConfig.threads);
    setTaskCount(queueConfig.taskCount);
    setInterval(queueConfig.interval);
    setRunMode(queueConfig.runMode);
    setStopCondition(queueConfig.stopCondition);
    setStopValue(queueConfig.stopValue);
  }, [queueConfig]);

  // 处理线程数量变更
  const handleThreadsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(10, Math.max(1, parseInt(e.target.value) || 1));
    setThreads(value);
    onQueueConfigChange({
      ...queueConfig,
      threads: value
    });
  };

  // 处理任务数量变更
  const handleTaskCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(100, Math.max(1, parseInt(e.target.value) || 1));
    setTaskCount(value);
    onQueueConfigChange({
      ...queueConfig,
      taskCount: value
    });
  };

  // 处理间隔时间变更
  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(60, Math.max(1, parseInt(e.target.value) || 1));
    setInterval(value);
    onQueueConfigChange({
      ...queueConfig,
      interval: value
    });
  };

  // 处理运行模式变更
  const handleRunModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as QueueRunMode;
    setRunMode(value);
    onQueueConfigChange({
      ...queueConfig,
      runMode: value
    });
  };

  // 处理停止条件变更
  const handleStopConditionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as QueueStopCondition;
    setStopCondition(value);
    onQueueConfigChange({
      ...queueConfig,
      stopCondition: value
    });
  };

  // 处理停止值变更
  const handleStopValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.max(1, parseInt(e.target.value) || 1);
    setStopValue(value);
    onQueueConfigChange({
      ...queueConfig,
      stopValue: value
    });
  };

  // 获取停止值的单位标签
  const getStopValueLabel = (): string => {
    switch (stopCondition) {
      case QueueStopCondition.RUN_COUNT:
        return '次';
      case QueueStopCondition.PRICE_INCREASE:
      case QueueStopCondition.PRICE_DECREASE:
        return '%';
      default:
        return '';
    }
  };

  return (
    <Paper 
      elevation={0}
      sx={{
        padding: 1.5,
        borderRadius: 2,
        marginBottom: 2,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1rem' }}>
          批量交换设置
        </Typography>
        <Tooltip title="设置批量执行交换任务的参数，包括线程数、任务数量和间隔时间">
          <InfoOutlinedIcon fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
        </Tooltip>
      </Box>
      
      <Divider sx={{ my: 1 }} />
      
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          批量执行交换任务，可设置线程数、任务数量和间隔时间。
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          label="线程数量"
          type="number"
          size="small"
          value={threads}
          onChange={handleThreadsChange}
          inputProps={{ min: 1, }}
          disabled={disabled || taskRunning}
          sx={{ width: '30%', minWidth: '120px' }}
        />
        
        <TextField
          label="任务数量"
          type="number"
          size="small"
          value={taskCount}
          onChange={handleTaskCountChange}
          inputProps={{ min: 1}}
          disabled={disabled || taskRunning}
          sx={{ width: '30%', minWidth: '120px' }}
        />
        
        <TextField
          label="间隔时间"
          type="number"
          size="small"
          value={interval}
          onChange={handleIntervalChange}
          InputProps={{
            endAdornment: <InputAdornment position="end">秒</InputAdornment>,
          }}
          inputProps={{ min: 1}}
          disabled={disabled || taskRunning}
          sx={{ width: '30%', minWidth: '120px' }}
        />
      </Box>
      
      <Box sx={{ display: 'flex', gap: 3, mb: 1.5 }}>
        <FormControl component="fieldset" disabled={disabled || taskRunning}>
          <FormLabel component="legend">队列运行模式</FormLabel>
          <RadioGroup
            row
            value={runMode}
            onChange={handleRunModeChange}
          >
            <FormControlLabel value={QueueRunMode.SERIAL} control={<Radio size="small" />} label="串行" />
            <FormControlLabel value={QueueRunMode.PARALLEL} control={<Radio size="small" />} label="并行" />
          </RadioGroup>
        </FormControl>
        
        <FormControl component="fieldset" disabled={disabled || taskRunning}>
          <FormLabel component="legend">停止方式</FormLabel>
          <RadioGroup
            row
            value={stopCondition}
            onChange={handleStopConditionChange}
          >
            <FormControlLabel value={QueueStopCondition.RUN_COUNT} control={<Radio size="small" />} label="运行次数" />
            <FormControlLabel value={QueueStopCondition.PRICE_INCREASE} control={<Radio size="small" />} label="价格上涨时" />
            <FormControlLabel value={QueueStopCondition.PRICE_DECREASE} control={<Radio size="small" />} label="价格下跌时" />
          </RadioGroup>
        </FormControl>
      </Box>
      
      <Box sx={{ mb: 1.5 }}>
        <TextField
          label={`停止值 (${getStopValueLabel()})`}
          type="number"
          size="small"
          value={stopValue}
          onChange={handleStopValueChange}
          InputProps={{
            endAdornment: <InputAdornment position="end">{getStopValueLabel()}</InputAdornment>,
          }}
          inputProps={{ min: 1 }}
          disabled={disabled || taskRunning}
          sx={{ width: '30%', minWidth: '120px' }}
        />
      </Box>
      
      <Alert severity="info" sx={{ fontSize: '0.85rem' }}>
        批量任务将{runMode === QueueRunMode.PARALLEL ? '按照设置的线程数并发执行' : '按照设置的间隔时间串行执行'}，请确保余额充足。
        {stopCondition !== QueueStopCondition.RUN_COUNT && '当价格变化达到设定值时，将自动停止执行。'}
      </Alert>
    </Paper>
  );
};

export default BatchTaskSettings;
